import React, { useState } from 'react';
import { Accommodation, AccommodationType, ParkingSpot } from '../types';
import { formatCurrencyValue, normalizeString, asArray } from '../utils/storage';
import { 
  Plus, 
  Settings, 
  Home, 
  MapPin, 
  Edit, 
  X, 
  CornerDownRight, 
  ToggleLeft,
  ToggleRight,
  ShieldCheck,
  CheckSquare,
  Square,
  Search
} from 'lucide-react';

interface AccommodationsProps {
  accommodations: Accommodation[];
  accommodationTypes: AccommodationType[];
  parkingSpots: ParkingSpot[];
  onAddAccommodation: (acc: Omit<Accommodation, 'id'>) => void;
  onUpdateAccommodation: (id: string, acc: Partial<Accommodation>) => void;
  onAddAccommodationType: (name: string) => void;
  onUpdateAccommodationType: (id: string, active: boolean) => void;
  isAdmin: boolean;
}

export default function Accommodations({ 
  accommodations, 
  accommodationTypes, 
  parkingSpots, 
  onAddAccommodation, 
  onUpdateAccommodation, 
  onAddAccommodationType, 
  onUpdateAccommodationType,
  isAdmin
}: AccommodationsProps) {
  const [search, setSearch] = useState('');
  // Modals
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccForEdit, setSelectedAccForEdit] = useState<Accommodation | null>(null);

  // Form states - Accommodation Type
  const [newTypeName, setNewTypeName] = useState('');
  const [typeError, setTypeError] = useState('');

  // Form states - Accommodation
  const [accName, setAccName] = useState('');
  const [accNumber, setAccNumber] = useState('');
  const [accTypeId, setAccTypeId] = useState('');
  const [accRate, setAccRate] = useState('');
  const [accCapacity, setAccCapacity] = useState('');
  const [accDescription, setAccDescription] = useState('');
  const [accSelectedSpots, setAccSelectedSpots] = useState<string[]>([]);
  const [accError, setAccError] = useState('');

  // Handle addition of type
  const handleAddType = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTypeName.trim()) {
      setTypeError('Insira um nome válido para o tipo.');
      return;
    }
    const dup = accommodationTypes.some(t => t.name.toLowerCase() === newTypeName.trim().toLowerCase());
    if (dup) {
      setTypeError('Este tipo de acomodação já existe.');
      return;
    }
    onAddAccommodationType(newTypeName.trim());
    setNewTypeName('');
    setTypeError('');
  };

  // Open add accommodation modal
  const openAddAccModal = () => {
    // Check if we have at least 1 accommodation type
    const activeTypes = accommodationTypes.filter(t => t.active);
    if (activeTypes.length === 0) {
      alert("Por favor, adicione pelo menos um Tipo de Acomodação ativo antes de registrar os quartos!");
      setShowTypeModal(true);
      return;
    }
    
    setAccName('');
    setAccNumber('');
    setAccTypeId(activeTypes[0].id);
    setAccRate('');
    setAccCapacity('2');
    setAccDescription('');
    setAccSelectedSpots([]);
    setAccError('');
    setShowAddModal(true);
  };

  const handleAddAccommodation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!accName.trim() || !accNumber.trim() || !accRate) {
      setAccError('Por favor, preencha todos os campos obrigatórios (*).');
      return;
    }
    const rateNum = parseFloat(accRate);
    if (isNaN(rateNum) || rateNum <= 0) {
      setAccError('O valor da diária deve ser um número positivo.');
      return;
    }

    // Verify duplicate number
    const dup = accommodations.some(a => a.number.trim() === accNumber.trim());
    if (dup) {
      setAccError('Já existe uma acomodação cadastrada com este número de quarto.');
      return;
    }

    onAddAccommodation({
      name: accName.trim(),
      number: accNumber.trim(),
      typeId: accTypeId,
      rate: rateNum,
      capacity: parseInt(accCapacity) || 2,
      description: accDescription.trim(),
      parkingSpots: accSelectedSpots,
      active: true
    });

    setShowAddModal(false);
  };

  // Open edit accommodation modal
  const openEditAccModal = (acc: Accommodation) => {
    setSelectedAccForEdit(acc);
    setAccName(acc.name);
    setAccNumber(acc.number);
    setAccTypeId(acc.typeId);
    setAccRate(String(acc.rate));
    setAccCapacity(String(acc.capacity));
    setAccDescription(acc.description);
    setAccSelectedSpots(asArray(acc.parkingSpots));
    setAccError('');
    setShowEditModal(true);
  };

  const handleEditAccommodation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccForEdit) return;
    if (!accName.trim() || !accNumber.trim() || !accRate) {
      setAccError('Por favor, preencha todos os campos obrigatórios (*).');
      return;
    }
    const rateNum = parseFloat(accRate);
    if (isNaN(rateNum) || rateNum <= 0) {
      setAccError('O valor da diária deve ser maior que zero.');
      return;
    }

    // Verify duplicate number (excluding self)
    const dup = accommodations.some(a => a.number.trim() === accNumber.trim() && a.id !== selectedAccForEdit.id);
    if (dup) {
      setAccError('Outro quarto já possui este número cadastrado.');
      return;
    }

    onUpdateAccommodation(selectedAccForEdit.id, {
      name: accName.trim(),
      number: accNumber.trim(),
      typeId: accTypeId,
      rate: rateNum,
      capacity: parseInt(accCapacity) || 2,
      description: accDescription.trim(),
      parkingSpots: accSelectedSpots
    });

    setShowEditModal(false);
  };

  const toggleSpotSelection = (spotNum: string) => {
    setAccSelectedSpots(prev => {
      if (prev.includes(spotNum)) {
        return prev.filter(s => s !== spotNum);
      }
      return [...prev, spotNum];
    });
  };

  // Computed
  const filteredAccommodations = accommodations.filter(acc => {
    const normalizedAcc = normalizeString(`${acc.name} ${acc.number} ${acc.description}`);
    const normalizedSearch = normalizeString(search);
    return normalizedAcc.includes(normalizedSearch);
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-5">
        <div>
          <h1 id="header-title-accommodations" className="text-xl font-semibold tracking-tight text-zinc-950">
            Acomodações
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Cadastre as acomodações, controle diárias e vincule as vagas de estacionamento da pousada.
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            id="btn-manage-types-trigger"
            onClick={() => {
              setNewTypeName('');
              setTypeError('');
              setShowTypeModal(true);
            }}
            className="flex items-center gap-1.5 px-3.5 py-2 border border-zinc-200 hover:border-zinc-300 rounded-lg text-zinc-700 bg-white font-semibold text-xs cursor-pointer shadow-sm"
          >
            <Settings className="w-4 h-4 text-zinc-500" /> Tipo de acomodação
          </button>
          
          <button
            type="button"
            id="btn-add-acc-trigger"
            onClick={openAddAccModal}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer shadow-sm active:scale-[0.98]"
          >
            <Plus className="w-4 h-4" /> Acomodação
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 border border-zinc-200 rounded-xl shadow-sm">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-400">
            <Search className="w-4 h-4" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-colors"
            placeholder="Buscar por nome, número do quarto ou descrição..."
          />
        </div>
      </div>

      {/* Main Table View */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        <div className="border-b border-zinc-100 bg-zinc-50 px-5 py-4">
          <h2 className="text-xs font-bold text-zinc-800 uppercase tracking-wider">
            Acomodações Cadastradas
          </h2>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table id="table-accommodations" className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-zinc-50/50 border-b border-zinc-200 text-zinc-500 font-bold uppercase tracking-wider text-xxs">
                <th className="py-3 px-4 w-12 text-center">#</th>
                <th className="py-3 px-4">Acomodação</th>
                <th className="py-3 px-4">Nº Quarto</th>
                <th className="py-3 px-4">Tipo</th>
                <th className="py-3 px-4">Valor Diária</th>
                <th className="py-3 px-4 text-center">Capacidade</th>
                <th className="py-3 px-4">Vagas Garagem</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-right w-24">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200/60 font-medium text-zinc-700">
              {filteredAccommodations.length === 0 ? (
                <tr>
                  <td colSpan={9} id="acc-empty-row" className="p-8 text-center text-zinc-400 font-normal">
                    Nenhuma acomodação encontrada com os termos da busca.
                  </td>
                </tr>
              ) : (
                filteredAccommodations.map((acc, idx) => {
                  const typeName = accommodationTypes.find(t => t.id === acc.typeId)?.name || 'Outro';
                  return (
                    <tr key={acc.id} id={`acc-row-${acc.id}`} className="hover:bg-zinc-50/50 transition-colors">
                      <td className="py-3.5 px-4 font-mono text-zinc-400 text-xxs text-center">{(idx + 1).toString().padStart(2, '0')}</td>
                      <td className="py-3.5 px-4 pr-2">
                        <div className="font-semibold text-zinc-900 text-xs flex items-center gap-1.5">
                          <Home className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                          <span>{acc.name}</span>
                        </div>
                        <div className="text-xxs text-zinc-400 font-normal mt-0.5 line-clamp-1 max-w-sm">{acc.description || 'Sem descrição.'}</div>
                      </td>
                      <td className="py-3.5 px-4 font-mono text-xs">{acc.number}</td>
                      <td className="py-3.5 px-4 text-xs font-semibold text-zinc-500">{typeName}</td>
                      <td className="py-3.5 px-4 font-mono text-xs font-bold text-zinc-900">{formatCurrencyValue(acc.rate)}</td>
                      <td className="py-3.5 px-4 text-center">{acc.capacity} hóspedes</td>
                      <td className="py-3.5 px-4 font-normal">
                        {Array.isArray(acc.parkingSpots) && acc.parkingSpots.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {acc.parkingSpots.map((s: string) => (
                              <span key={s} className="bg-zinc-100 text-zinc-700 border border-zinc-200 text-xxs px-1.5 py-0.5 rounded font-mono font-semibold">
                                Vaga {s}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-zinc-400 text-xxs">Nenhuma vaga</span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-center text-xxs">
                        <div className="flex justify-center">
                          {acc.active ? (
                            <span id={`acc-status-badge-${acc.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-green-50 text-green-700 border border-green-200">
                              Ativo
                            </span>
                          ) : (
                            <span id={`acc-status-badge-${acc.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200">
                              Inativo
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            id={`btn-edit-acc-${acc.id}`}
                            onClick={() => openEditAccModal(acc)}
                            className="p-1 text-zinc-600 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-300 bg-white rounded cursor-pointer transition-colors"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          
                          <button
                            type="button"
                            id={`btn-toggle-acc-${acc.id}`}
                            onClick={() => {
                              if (!isAdmin) {
                                alert("Apenas Administradores podem desativar acomodações.");
                                return;
                              }
                              onUpdateAccommodation(acc.id, { active: !acc.active });
                            }}
                            className={`p-1 rounded border transition-colors cursor-pointer ${
                              acc.active 
                                ? 'text-green-600 hover:text-green-800 border-green-200 bg-green-50/10' 
                                : 'text-red-600 hover:text-red-800 border-red-200 bg-red-50/10'
                            }`}
                          >
                            {acc.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card List View */}
        <div className="md:hidden divide-y divide-zinc-200">
          {filteredAccommodations.length === 0 ? (
            <div id="acc-empty-mobile" className="p-8 text-center text-zinc-400 font-normal text-xs bg-white">
              Nenhuma acomodação encontrada com os termos da busca.
            </div>
          ) : (
            filteredAccommodations.map((acc, idx) => {
              const typeName = accommodationTypes.find(t => t.id === acc.typeId)?.name || 'Outro';
              return (
                <div key={acc.id} id={`acc-card-mobile-${acc.id}`} className="p-4 bg-white space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Home className="w-4 h-4 text-zinc-500 shrink-0" />
                      <span className="font-semibold text-zinc-900 text-sm">{acc.name}</span>
                    </div>
                    <span className="bg-zinc-150 text-zinc-700 font-mono text-[11px] px-2 py-0.5 rounded border border-zinc-200 font-semibold shrink-0">
                      Nº {acc.number}
                    </span>
                  </div>

                  {acc.description && (
                    <div className="text-[11px] text-zinc-500 leading-normal line-clamp-1 italic">
                      "{acc.description}"
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-[11px] text-zinc-650 bg-zinc-50 p-2.5 rounded-lg border border-zinc-150">
                    <div>
                      <span className="block font-bold text-zinc-400 uppercase text-[9px] tracking-wide mb-0.5">Tipo/Diária</span>
                      <div className="font-semibold text-zinc-850">{typeName}</div>
                      <div className="text-zinc-600 font-mono font-bold">{formatCurrencyValue(acc.rate)}/dia</div>
                    </div>
                    <div>
                      <span className="block font-bold text-zinc-400 uppercase text-[9px] tracking-wide mb-0.5">Atribuições</span>
                      <div className="font-semibold text-zinc-850">{acc.capacity} hóspedes</div>
                      <div className="text-zinc-500 font-normal">
                        {Array.isArray(acc.parkingSpots) && acc.parkingSpots.length > 0 ? (
                          <span className="text-zinc-700 font-mono font-bold">Vaga {acc.parkingSpots.join(', ')}</span>
                        ) : (
                          'Sem vaga vinculante'
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t border-zinc-100 pt-2.5">
                    <div>
                      {acc.active ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-50 text-green-700 border border-green-200">
                          Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200">
                          Inativo
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        id={`btn-mobile-edit-acc-${acc.id}`}
                        onClick={() => openEditAccModal(acc)}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-lg text-zinc-700 bg-white text-xxs font-bold uppercase cursor-pointer hover:bg-zinc-50"
                      >
                        <Edit className="w-3.5 h-3.5 text-zinc-500" />
                        <span>Editar</span>
                      </button>
                      
                      <button
                        type="button"
                        id={`btn-mobile-toggle-acc-${acc.id}`}
                        onClick={() => {
                          if (!isAdmin) {
                            alert("Apenas Administradores podem desativar acomodações.");
                            return;
                          }
                          onUpdateAccommodation(acc.id, { active: !acc.active });
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xxs font-bold uppercase transition-colors cursor-pointer ${
                          acc.active 
                            ? 'text-green-600 hover:text-green-800 border-green-200 bg-green-50/25' 
                            : 'text-red-600 hover:text-red-800 border-red-200 bg-red-50/25'
                        }`}
                      >
                        {acc.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                        <span>{acc.active ? 'Ativo' : 'Inativo'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

      </div>

      {/* Modal: MANAGE TYPES */}
      {showTypeModal && (
        <div id="type-modal" className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 animate-fade-in overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-md w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-zinc-300" />
                <h3 className="text-xs font-bold uppercase tracking-wider">Tipo de acomodação</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowTypeModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-5 overflow-y-auto flex-1">
              {/* Type creation form */}
              <form onSubmit={handleAddType} className="flex gap-2 items-start">
                <div className="flex-1">
                  <input
                    type="text"
                    id="input-new-type-name"
                    value={newTypeName}
                    onChange={(e) => {
                      setNewTypeName(e.target.value);
                      setTypeError('');
                    }}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                    placeholder="Nome do Tipo (Ex. Suíte, Chalé...)"
                    required
                  />
                  {typeError && (
                    <span className="text-xxs text-red-600 mt-1 block font-medium">{typeError}</span>
                  )}
                </div>
                <button
                  type="submit"
                  id="btn-register-type-submit"
                  className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap active:scale-[0.98]"
                >
                  Cadastrar
                </button>
              </form>

              {/* Type list */}
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                <span className="block text-xxs font-bold text-zinc-400 uppercase tracking-wider border-b border-zinc-100 pb-1.5">Tipos Registrados:</span>
                {accommodationTypes.length === 0 ? (
                  <span className="block text-xxs text-zinc-400 py-4 text-center">Nenhum tipo cadastrado.</span>
                ) : (
                  accommodationTypes.map(type => (
                    <div key={type.id} className="flex items-center justify-between p-2 bg-zinc-50 hover:bg-zinc-100/70 border border-zinc-200/50 rounded-lg text-xs transition-colors">
                      <span className="font-semibold text-zinc-800">{type.name}</span>
                      <div className="flex items-center gap-2">
                        {type.active ? (
                          <span className="text-[10px] font-bold text-green-700 bg-green-50 px-1.5 py-0.2 rounded border border-green-200 border-none uppercase">Ativo</span>
                        ) : (
                          <span className="text-[10px] font-semibold text-zinc-400 bg-zinc-100 px-1.5 py-0.2 rounded border border-zinc-200 border-none uppercase">Inativo</span>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (!isAdmin) {
                              alert("Apenas administradores podem gerenciar tipos de acomodação.");
                              return;
                            }
                            onUpdateAccommodationType(type.id, !type.active);
                          }}
                          className={`p-1 border rounded text-xxs cursor-pointer ${
                            type.active 
                              ? 'text-green-600 border-green-100 hover:bg-green-50' 
                              : 'text-zinc-400 border-zinc-200 hover:bg-zinc-100'
                          }`}
                        >
                          {type.active ? "Desativar" : "Ativar"}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: ADD ACCOMMODATION */}
      {showAddModal && (
        <div id="add-accommodation-modal" className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-lg lg:max-w-2xl xl:max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Home className="w-5 h-5 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Cadastrar nova Acomodação</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddAccommodation} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {accError && (
                <div className="p-2.5 bg-red-50 text-red-700 text-xxs font-semibold border border-red-100 rounded-lg">
                  {accError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="form-acc-name" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Nome da Acomodação *</label>
                  <input
                    type="text"
                    id="form-acc-name"
                    value={accName}
                    onChange={(e) => setAccName(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    placeholder="Ex: Suíte Master Vista Mar"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="form-acc-num" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Número do Quarto *</label>
                  <input
                    type="text"
                    id="form-acc-num"
                    value={accNumber}
                    onChange={(e) => setAccNumber(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    placeholder="Ex: 101"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-1">
                  <label htmlFor="form-acc-type" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Tipo *</label>
                  <select
                    id="form-acc-type"
                    value={accTypeId}
                    onChange={(e) => setAccTypeId(e.target.value)}
                    className="w-full text-xs py-2 px-2 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  >
                    {accommodationTypes.filter(t => t.active).map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="col-span-1">
                  <label htmlFor="form-acc-rate" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Valor (diária) *</label>
                  <input
                    type="number"
                    step="0.01"
                    id="form-acc-rate"
                    value={accRate}
                    onChange={(e) => setAccRate(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    placeholder="R$ 350,00"
                    required
                  />
                </div>

                <div className="col-span-1">
                  <label htmlFor="form-acc-capacity" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Capacidade *</label>
                  <input
                    type="number"
                    id="form-acc-capacity"
                    value={accCapacity}
                    onChange={(e) => setAccCapacity(e.target.value)}
                    min={1}
                    max={10}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="form-acc-desc" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Descrição</label>
                <textarea
                  id="form-acc-desc"
                  value={accDescription}
                  onChange={(e) => setAccDescription(e.target.value)}
                  rows={2}
                  className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  placeholder="Descrição da acomodação ou detalhes do mobiliário..."
                />
              </div>

              {/* Parking Selection */}
              <div className="space-y-2 p-3 bg-zinc-50 border border-zinc-200 rounded-xl">
                <span className="block text-xxs font-bold text-zinc-500 uppercase tracking-wider">Vagas de Estacionamento</span>
                <span className="block text-[10px] text-zinc-400 leading-normal mb-2">Para vincular uma vaga de estacionamento à acomodação, é necessário que a vaga em questão esteja cadastrada, ativa e disponível (módulo de “Estacionamento”).</span>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-h-36 overflow-y-auto p-1">
                  {parkingSpots.filter(p => p.active).map(spot => {
                    const isSelected = accSelectedSpots.includes(spot.number);
                    const isOccupiedByAnother = spot.status === 'Ocupado' && accommodations.some(a => Array.isArray(a.parkingSpots) && a.parkingSpots.includes(spot.number));
                    
                    return (
                      <button
                        type="button"
                        key={spot.id}
                        onClick={() => toggleSpotSelection(spot.number)}
                        className={`flex items-center gap-2 p-2 border rounded-lg text-xxs font-semibold transition-all cursor-pointer ${
                          isSelected
                            ? 'border-zinc-950 bg-zinc-950 text-white'
                            : 'border-zinc-200 hover:border-zinc-300 text-zinc-700 bg-white'
                        }`}
                      >
                        {isSelected ? <CheckSquare className="w-3.5 h-3.5 shrink-0" /> : <Square className="w-3.5 h-3.5 shrink-0" />}
                        <span>Vaga {spot.number}</span>
                      </button>
                    );
                  })}
                  {parkingSpots.filter(p => p.active).length === 0 && (
                    <span className="text-[10px] text-zinc-400 py-2 col-span-full text-center">Nenhuma vaga de garagem ativa cadastrada.</span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-700 font-semibold text-xs cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  id="btn-submit-acc-add"
                  className="px-5 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm"
                >
                  Cadastrar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: EDIT ACCOMMODATION */}
      {showEditModal && selectedAccForEdit && (
        <div id="edit-accommodation-modal" className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-lg lg:max-w-2xl xl:max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Edit className="w-4 h-4 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Editar acomodação: {selectedAccForEdit.number}</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditAccommodation} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {accError && (
                <div className="p-2.5 bg-red-50 text-red-700 text-xxs font-semibold border border-red-100 rounded-lg">
                  {accError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="form-acc-edit-name" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Nome da Acomodação *</label>
                  <input
                    type="text"
                    id="form-acc-edit-name"
                    value={accName}
                    onChange={(e) => setAccName(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="form-acc-edit-num" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Número do Quarto *</label>
                  <input
                    type="text"
                    id="form-acc-edit-num"
                    value={accNumber}
                    onChange={(e) => setAccNumber(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label htmlFor="form-acc-edit-type" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Tipo *</label>
                  <select
                    id="form-acc-edit-type"
                    value={accTypeId}
                    onChange={(e) => setAccTypeId(e.target.value)}
                    className="w-full text-xs py-2 px-2 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  >
                    {accommodationTypes.filter(t => t.active).map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="form-acc-edit-rate" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Valor (diária) *</label>
                  <input
                    type="number"
                    step="0.01"
                    id="form-acc-edit-rate"
                    value={accRate}
                    onChange={(e) => setAccRate(e.target.value)}
                    className="w-full text-xs py-2 px-2 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="form-acc-edit-capacity" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Capacidade *</label>
                  <input
                    type="number"
                    id="form-acc-edit-capacity"
                    value={accCapacity}
                    onChange={(e) => setAccCapacity(e.target.value)}
                    min={1}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="form-acc-edit-desc" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Descrição</label>
                <textarea
                  id="form-acc-edit-desc"
                  value={accDescription}
                  onChange={(e) => setAccDescription(e.target.value)}
                  rows={2}
                  className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                />
              </div>

              {/* Parking Selection */}
              <div className="space-y-2 p-3 bg-zinc-50 border border-zinc-200 rounded-xl">
                <span className="block text-xxs font-bold text-zinc-500 uppercase tracking-wider">Vagas de Estacionamento</span>
                <span className="block text-[10px] text-zinc-400 mb-2 leading-normal">Selecione uma ou mais vagas ativas de garagem para vincular a esta unidade.</span>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-h-36 overflow-y-auto p-1">
                  {parkingSpots.filter(p => p.active).map(spot => {
                    const isSelected = accSelectedSpots.includes(spot.number);
                    return (
                      <button
                        type="button"
                        key={spot.id}
                        onClick={() => toggleSpotSelection(spot.number)}
                        className={`flex items-center gap-2 p-2 border rounded-lg text-xxs font-semibold transition-all cursor-pointer ${
                          isSelected
                            ? 'border-zinc-950 bg-zinc-950 text-white'
                            : 'border-zinc-200 hover:border-zinc-300 text-zinc-700 bg-white'
                        }`}
                      >
                        {isSelected ? <CheckSquare className="w-3.5 h-3.5 shrink-0" /> : <Square className="w-3.5 h-3.5 shrink-0" />}
                        <span>Vaga {spot.number}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-700 font-semibold text-xs cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  id="btn-edit-acc-submit"
                  className="px-5 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm animate-pulse-once"
                >
                  Editar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
